<template>
  <div
    v-if="modelValue"
    class="ncrm-mailjet-sender-modal"
    data-mailjet-sender-dialog-version="2026-05-14-html-modal"
    role="dialog"
    aria-modal="true"
  >
    <div class="ncrm-mailjet-sender-modal__scrim" />
    <section class="ncrm-mailjet-sender-modal__panel">
      <header class="ncrm-mailjet-sender-modal__header">
        {{ $t("ncrm.integrations.mailjet.dialog.senderSectionTitle") }}
      </header>
      <div class="ncrm-mailjet-sender-modal__body">
        <p class="ncrm-mailjet-sender-modal__account">
          {{ accountName }}
        </p>
        <label class="ncrm-mailjet-sender-modal__label" for="mailjet-default-sender-email">
          {{ $t("ncrm.integrations.mailjet.dialog.fields.defaultSenderEmail") }}
        </label>
        <input
          id="mailjet-default-sender-email"
          v-model="defaultSenderEmail"
          class="ncrm-mailjet-sender-modal__input"
          type="email"
          autocomplete="off"
          :placeholder="$t('ncrm.integrations.mailjet.dialog.fields.defaultSenderEmailPlaceholder')"
        >
        <div class="ncrm-mailjet-sender-modal__hint">
          {{ $t("ncrm.integrations.mailjet.dialog.fields.defaultSenderEmailHint") }}
        </div>
        <label class="ncrm-mailjet-sender-modal__label ncrm-mailjet-sender-modal__label--spaced" for="mailjet-default-sender-name">
          {{ $t("ncrm.integrations.mailjet.dialog.fields.defaultSenderName") }}
        </label>
        <input
          id="mailjet-default-sender-name"
          v-model="defaultSenderName"
          class="ncrm-mailjet-sender-modal__input"
          type="text"
          autocomplete="off"
        >
        <div v-if="error" class="ncrm-mailjet-sender-modal__error">
          {{ error }}
        </div>
      </div>
      <footer class="ncrm-mailjet-sender-modal__actions">
        <button
          type="button"
          class="ncrm-mailjet-sender-modal__button"
          :disabled="saving"
          @click="close"
        >
          {{ $t("ncrm.integrations.mailjet.dialog.actions.cancel") }}
        </button>
        <button
          type="button"
          class="ncrm-mailjet-sender-modal__button ncrm-mailjet-sender-modal__button--primary"
          :disabled="saving"
          @click="submit"
        >
          {{ $t("ncrm.integrations.mailjet.dialog.actions.save") }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmMailjetSenderDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    account: { type: Object, default: null },
  },
  emits: ["update:modelValue", "notify", "saved"],
  data() {
    return {
      saving: false,
      error: "",
      defaultSenderEmail: "",
      defaultSenderName: "",
    };
  },
  computed: {
    accountName() {
      return this.account?.name || "";
    },
  },
  watch: {
    account: {
      immediate: true,
      handler() {
        this.resetFields();
      },
    },
    modelValue(value) {
      if (value) this.resetFields();
    },
  },
  methods: {
    resetFields() {
      this.error = "";
      this.defaultSenderEmail = this.account?.default_sender_email || "";
      this.defaultSenderName = this.account?.default_sender_name || "";
    },
    close() {
      if (this.saving) return;
      this.$emit("update:modelValue", false);
    },
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    async submit() {
      if (!this.account?.id) return;
      this.saving = true;
      this.error = "";
      try {
        await axios.patch(`/ncrm/integrations/mailjet/accounts/${this.account.id}`, {
          defaultSenderEmail: this.defaultSenderEmail?.trim() || null,
          defaultSenderName: this.defaultSenderName?.trim() || null,
        });
        this.$emit("notify", {
          message: this.$t("ncrm.integrations.mailjet.snackbar.updated"),
          color: "success",
        });
        this.$emit("update:modelValue", false);
        this.$emit("saved");
      } catch (error) {
        this.error = this.extractError(
          error,
          "ncrm.integrations.mailjet.snackbar.saveError"
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-sender-modal {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.ncrm-mailjet-sender-modal__scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
}
.ncrm-mailjet-sender-modal__panel {
  position: relative;
  z-index: 1;
  width: min(560px, calc(100vw - 32px));
  overflow: hidden;
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
}
.ncrm-mailjet-sender-modal__header {
  padding: 22px 28px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  color: var(--app-text-strong);
  font-size: 1.35rem;
  font-weight: 500;
}
.ncrm-mailjet-sender-modal__body {
  padding: 24px 28px;
}
.ncrm-mailjet-sender-modal__account {
  margin: 0 0 18px;
  color: rgba(var(--v-theme-on-surface), 0.68);
}
.ncrm-mailjet-sender-modal__label {
  display: block;
  color: rgba(var(--v-theme-on-surface), 0.78);
  font-size: 0.82rem;
  margin-bottom: 4px;
}
.ncrm-mailjet-sender-modal__label--spaced {
  margin-top: 18px;
}
.ncrm-mailjet-sender-modal__input {
  width: 100%;
  min-height: 48px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.34);
  border-radius: 8px;
  color: var(--app-text-strong);
  background: rgb(var(--v-theme-surface));
  font: inherit;
  padding: 10px 14px;
  outline: none;
}
.ncrm-mailjet-sender-modal__input:focus {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}
.ncrm-mailjet-sender-modal__hint {
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 0.75rem;
  margin: 4px 0 0 14px;
}
.ncrm-mailjet-sender-modal__error {
  margin-top: 14px;
  border-radius: 8px;
  background: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-error));
  padding: 10px 12px;
}
.ncrm-mailjet-sender-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}
.ncrm-mailjet-sender-modal__button {
  min-width: 82px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-strong);
  cursor: pointer;
  font: inherit;
  padding: 8px 14px;
}
.ncrm-mailjet-sender-modal__button:hover {
  background: rgba(var(--v-theme-on-surface), 0.08);
}
.ncrm-mailjet-sender-modal__button--primary {
  color: rgb(var(--v-theme-primary));
}
.ncrm-mailjet-sender-modal__button:disabled {
  cursor: default;
  opacity: 0.55;
}
</style>
